import type {
  BatchUploadResultDto,
  BatchValidationResultDto,
} from './generated';
import {
  batchControllerUpload,
  batchControllerValidate,
  photosControllerExportPhotos,
  photosControllerGetPhotoUrls,
} from './generated';
import type { IseeSessionQuery } from './isee';
import { getIseeStudents } from './isee';

export type ValidationResult = BatchValidationResultDto;
export type BatchResult = BatchUploadResultDto;

/** Check which UPNs exist in Entra before uploading anything. */
export async function validateBatch(
  upns: string[],
): Promise<ValidationResult[]> {
  const { data } = await batchControllerValidate({
    body: { upns },
    throwOnError: true,
  });
  return data;
}

/**
 * Upload multiple JPEG or PNG photos in a single request.
 * Each file's basename (without extension) is used as the UPN for matching.
 */
export async function uploadBatchPhotos(files: File[]): Promise<BatchResult[]> {
  const { data } = await batchControllerUpload({
    body: { photo: files },
    throwOnError: true,
  });
  return data;
}

/** ZIP of the photos of an ISEE cohort, one `<upn>.jpg` per student. Admin only. */
export async function exportPhotos(query: IseeSessionQuery): Promise<Blob> {
  const { data } = await photosControllerExportPhotos({
    query,
    throwOnError: true,
  });
  return data;
}

export interface PhotoRecapRow {
  login: string;
  name: string;
  hasPhoto: boolean;
}

/** `/photos/urls` takes up to this many logins per call (MAX_BATCH_UPNS). */
const PHOTO_URLS_MAX = 500;

/**
 * Who {@link exportPhotos} would cover and which of them has a photo, missing
 * ones first. Same student list and photo cache as the export, so the recap
 * and the ZIP agree.
 */
export async function photoRecap(
  query: IseeSessionQuery,
): Promise<PhotoRecapRow[]> {
  const { students } = await getIseeStudents(query);
  // A student in two sessions comes back twice; the export dedupes the same way.
  const names = new Map(
    students.map((s) => [
      s.login.trim().toLowerCase(),
      `${s.firstname} ${s.lastname}`.trim(),
    ]),
  );
  // `/photos/urls` refuses anything but full UPNs: the others have no photo.
  const upns = [...names.keys()].filter((login) => login.includes('@'));
  const batches = Array.from(
    { length: Math.ceil(upns.length / PHOTO_URLS_MAX) },
    (_, i) => upns.slice(i * PHOTO_URLS_MAX, (i + 1) * PHOTO_URLS_MAX),
  );
  const answers = await Promise.all(
    batches.map((logins) =>
      photosControllerGetPhotoUrls({ body: { logins }, throwOnError: true }),
    ),
  );
  const withPhoto = new Set(
    answers.flatMap(({ data }) =>
      data.filter((r) => r.url).map((r) => r.login),
    ),
  );
  return [...names]
    .map(([login, name]) => ({ login, name, hasPhoto: withPhoto.has(login) }))
    .sort((a, b) => Number(a.hasPhoto) - Number(b.hasPhoto));
}
